<template>
  <div v-if="isOpen" class="fixed inset-0 bg-white dark:bg-gray-900 z-[999]">
    <div class="flex flex-col h-full">
      <!-- Header with Logo and Close Button -->
      <div class="flex items-center justify-between p-4 border-b">
        <nuxt-link to="/" class="flex-shrink-0" @click="emit('close')">
          <img
            src="../../assets/images/logo-black.svg"
            class="h-7 w-auto"
            alt="Academic Alerts"
          />
        </nuxt-link>
        <button @click="emit('close')" class="p-2 hover:bg-gray-100 rounded-lg">
          <Icon name="heroicons:x-mark" class="w-6 h-6" />
        </button>
      </div>

      <!-- Rest of the navigation content remains the same -->
      <div class="flex-1 overflow-y-auto p-4">
        <div class="space-y-4">
          <template
            v-for="(link, index) in navigationConfig.mainNav"
            :key="index"
          >
            <!-- Regular Link -->
            <nuxt-link
              v-if="!link.items"
              :to="link.to"
              class="flex items-center p-3 rounded-lg hover:bg-gray-100"
              @click="emit('close')"
            >
              <Icon :name="link.icon" class="w-5 h-5 mr-3" />
              {{ link.text }}
            </nuxt-link>

            <!-- Megamenu with Collapsible Sections -->
            <div v-else-if="link.type === 'megamenu'" class="space-y-2">
              <div
                @click="toggleSection(link.text)"
                class="flex items-center justify-between p-3 rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <div class="flex items-center">
                  <Icon :name="link.icon" class="w-5 h-5 mr-3" />
                  <span class="font-medium">{{ link.text }}</span>
                </div>
                <Icon
                  :name="
                    expandedSections[link.text]
                      ? 'heroicons:chevron-up'
                      : 'heroicons:chevron-down'
                  "
                  class="w-5 h-5"
                />
              </div>
              <div v-show="expandedSections[link.text]" class="pl-11 space-y-2">
                <div v-for="section in link.items" :key="section.title">
                  <button
                    @click="toggleSubSection(`${link.text}-${section.title}`)"
                    class="flex items-center justify-between w-full p-2 rounded-lg hover:bg-gray-50"
                  >
                    <span class="font-medium text-sm">{{ section.title }}</span>
                    <Icon
                      :name="
                        expandedSections[`${link.text}-${section.title}`]
                          ? 'heroicons:chevron-up'
                          : 'heroicons:chevron-down'
                      "
                      class="w-4 h-4"
                    />
                  </button>
                  <div
                    v-show="expandedSections[`${link.text}-${section.title}`]"
                    class="pl-4 space-y-1 mt-1"
                  >
                    <nuxt-link
                      v-for="item in section.items"
                      :key="item.text"
                      :to="item.to"
                      class="block p-2 rounded-lg hover:bg-gray-100 text-gray-600 text-sm"
                      @click="emit('close')"
                    >
                      <div class="flex items-center">
                        <Icon
                          v-if="item.icon"
                          :name="item.icon"
                          class="w-4 h-4 mr-2"
                        />
                        {{ item.text }}
                      </div>
                    </nuxt-link>
                  </div>
                </div>
              </div>
            </div>

            <!-- Dropdown -->
            <div v-else-if="link.type === 'dropdown'" class="space-y-2">
              <button
                @click="toggleSection(link.text)"
                class="flex items-center justify-between w-full p-3 rounded-lg hover:bg-gray-100"
              >
                <div class="flex items-center">
                  <Icon :name="link.icon" class="w-5 h-5 mr-3" />
                  <span class="font-medium">{{ link.text }}</span>
                </div>
                <Icon
                  :name="
                    expandedSections[link.text]
                      ? 'heroicons:chevron-up'
                      : 'heroicons:chevron-down'
                  "
                  class="w-5 h-5"
                />
              </button>
              <div v-show="expandedSections[link.text]" class="pl-11 space-y-1">
                <nuxt-link
                  v-for="item in link.items"
                  :key="item.text"
                  :to="item.to"
                  class="block p-2 rounded-lg hover:bg-gray-100 text-gray-600 text-sm"
                  @click="emit('close')"
                >
                  <div class="flex items-center">
                    <Icon
                      v-if="item.icon"
                      :name="item.icon"
                      class="w-4 h-4 mr-2"
                    />
                    {{ item.text }}
                  </div>
                </nuxt-link>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Auth Buttons -->
      <div v-if="!user" class="p-4 border-t">
        <div class="space-y-2">
          <nuxt-link
            to="/auth/login"
            class="flex items-center justify-center w-full p-3 rounded-lg bg-primary text-white hover:bg-primary/90 transition-all"
            @click="emit('close')"
          >
            <Icon name="heroicons:user" class="w-5 h-5 mr-2" />
            Sign In
          </nuxt-link>
          <nuxt-link
            to="/auth/signup"
            class="flex items-center justify-center w-full p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-all dark:border-gray-700 dark:hover:bg-gray-800"
            @click="emit('close')"
          >
            <Icon name="heroicons:user-plus" class="w-5 h-5 mr-2" />
            Sign Up
          </nuxt-link>
        </div>
      </div>

      <!-- User Profile Section -->
      <div v-else class="p-4 border-t">
        <div
          class="flex items-center gap-3 px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-800 mb-3"
        >
          <div
            class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"
          >
            <span class="text-base font-medium text-primary">
              {{ user.email[0].toUpperCase() }}
            </span>
          </div>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-200">
            {{ user.email }}
          </span>
        </div>
        <div class="space-y-2">
          <nuxt-link
            to="/dashboard"
            class="flex items-center w-full p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
            @click="emit('close')"
          >
            <Icon
              name="heroicons:squares-2x2"
              class="w-5 h-5 mr-3 text-gray-400"
            />
            <span class="text-gray-700 dark:text-gray-200">Dashboard</span>
          </nuxt-link>
          <button
            @click="handleLogout"
            class="flex items-center w-full p-3 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/10 transition-all text-red-600 dark:text-red-400"
          >
            <Icon
              name="heroicons:arrow-right-on-rectangle"
              class="w-5 h-5 mr-3"
            />
            <span>Sign Out</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigationConfig } from "~/config/navigation";

defineProps<{
  isOpen: boolean;
  user?: any;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "logout"): void;
}>();

const expandedSections = ref<Record<string, boolean>>({});

const toggleSection = (sectionKey: string) => {
  expandedSections.value[sectionKey] = !expandedSections.value[sectionKey];
};

const toggleSubSection = (sectionKey: string) => {
  expandedSections.value[sectionKey] = !expandedSections.value[sectionKey];
};

const handleLogout = () => {
  emit("logout");
  emit("close");
};
</script>
